import React from 'react';

import styles from 'css/src/components/common/KeyboardShortcutsModal.module.scss';
import Modal from 'js/src/components/common/ui/modal';
import { IS_APPLE } from 'js/src/libs/platform-utils';

type Shortcut = {
    keys: string[];
    alternativeKeys?: string[];
    description: string;
};

type ShortcutSection = {
    title: string;
    shortcuts: Shortcut[];
};

const MOD_KEY = IS_APPLE ? 'Cmd' : 'Ctrl';
const ALT_KEY = IS_APPLE ? 'Option' : 'Alt';

export const getShortcutSections = (): ShortcutSection[] => [
    {
        title: 'Creator',
        shortcuts: [
            { keys: [MOD_KEY, 'Z'], description: 'Undo' },
            { keys: [MOD_KEY, 'Shift', 'Z'], description: 'Redo' },
            {
                keys: ['Backspace'],
                alternativeKeys: ['Delete'],
                description: 'Delete the selected asset',
            },
            { keys: [MOD_KEY, 'C'], description: 'Copy the selected element' },
            { keys: [MOD_KEY, 'V'], description: 'Paste the copied element' },
            { keys: ['Arrow keys'], description: 'Nudge the selected element' },
            {
                keys: ['Shift', 'Arrow keys'],
                description: 'Nudge the selected element further',
            },
            { keys: [MOD_KEY, '['], description: 'Send the selected element back one layer' },
            {
                keys: [MOD_KEY, ']'],
                description: 'Bring the selected element forward one layer',
            },
            {
                keys: [ALT_KEY, 'Up'],
                description: 'Move the text you are editing up within its group',
            },
            {
                keys: [ALT_KEY, 'Down'],
                description: 'Move the text you are editing down within its group',
            },
        ],
    },
    {
        title: 'Video preview',
        shortcuts: [
            { keys: ['Space'], alternativeKeys: ['K'], description: 'Play / pause' },
            { keys: ['M'], description: 'Mute / unmute' },
            { keys: ['Left'], alternativeKeys: ['J'], description: 'Back 10 seconds' },
            { keys: ['Right'], alternativeKeys: ['L'], description: 'Forward 10 seconds' },
            {
                keys: ['Shift', 'Left'],
                alternativeKeys: ['Shift', 'J'],
                description: 'Back one frame',
            },
            {
                keys: ['Shift', 'Right'],
                alternativeKeys: ['Shift', 'L'],
                description: 'Forward one frame',
            },
        ],
    },
];

type KeyComboProps = {
    keys: string[];
};

const KeyCombo: React.FC<KeyComboProps> = ({ keys }) =>
    keys.map((key, index) => (
        <React.Fragment key={key}>
            {index > 0 && (
                <span className={styles['keyboard-shortcuts-modal__key-separator']}>+</span>
            )}
            <kbd className={styles['keyboard-shortcuts-modal__key']}>{key}</kbd>
        </React.Fragment>
    ));

type Props = {
    showing: boolean;
    onHidden: VoidFunction;
};

export const KeyboardShortcutsModal = React.memo(function KeyboardShortcutsModal({
    showing,
    onHidden,
}: Props) {
    const sections = React.useMemo(getShortcutSections, []);
    return (
        <Modal
            showing={showing}
            onHidden={onHidden}
            title="Keyboard shortcuts"
            scrollable
            data-testid="keyboard-shortcuts-modal"
        >
            {sections.map((section) => (
                <div key={section.title} className="mb-l5-16">
                    <h3
                        className={`${styles['keyboard-shortcuts-modal__section-title']} font-l5 font-l5--overline`}
                    >
                        {section.title}
                    </h3>
                    <table className="table table-sm mb-l5-0">
                        <tbody>
                            {section.shortcuts.map((shortcut) => (
                                <tr key={shortcut.keys.join('+')}>
                                    <td>{shortcut.description}</td>
                                    <td className="text-end text-nowrap">
                                        <KeyCombo keys={shortcut.keys} />
                                        {shortcut.alternativeKeys && (
                                            <>
                                                <span
                                                    className={
                                                        styles[
                                                            'keyboard-shortcuts-modal__key-separator'
                                                        ]
                                                    }
                                                >
                                                    or
                                                </span>
                                                <KeyCombo keys={shortcut.alternativeKeys} />
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </Modal>
    );
});
